// SSE Service for real-time backend notifications
// Note: Cloudflare Pages has 30s timeout - auto-reconnect is EXPECTED behavior
class SSEService {
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity; // Always reconnect (normal behavior with Cloudflare)
  private reconnectDelay = 2000; // Start with 2s
  private isManualDisconnect = false;
  private lastConnectTime = 0;

  connect(token: string, onEvent: (event: string, data: any) => void) {
    // Use same-origin proxy route to avoid cross-origin/CORS in whitelabel domains.
    // EventSource does not support custom headers; pass token as query param instead.
    const url = `/api/v1/events/stream?token=${encodeURIComponent(token)}`;
    
    // Only log initial connection, not reconnects (reduce noise)
    if (this.reconnectAttempts === 0) {
      console.log('📡 Connecting to SSE:', url.replace(/token=[^&]+/, 'token=***'));
    }
    
    this.eventSource = new EventSource(url, {
      withCredentials: false,
    });
    this.isManualDisconnect = false;
    this.lastConnectTime = Date.now();

    // Connection established
    this.eventSource.addEventListener('connected', () => {
      // Only log first connection and recovery after failures
      if (this.reconnectAttempts === 0 || this.reconnectAttempts > 1) {
        console.log('✅ SSE Connected');
      }
      this.reconnectAttempts = 0;
      this.reconnectDelay = 2000;
    });

    // Account activated
    this.eventSource.addEventListener('account_activated', (e) => {
      console.log('🔔 [SSE] Raw event received: account_activated', e);
      const data = JSON.parse(e.data);
      console.log('🔔 [SSE] Parsed data:', data);
      onEvent('account_activated', data);
      console.log('🔔 [SSE] onEvent callback executed');
    });

    // Account failed
    this.eventSource.addEventListener('account_failed', (e) => {
      const data = JSON.parse(e.data);
      onEvent('account_failed', data);
    });

    // Workflow activated
    this.eventSource.addEventListener('workflow_activated', (e) => {
      const data = JSON.parse(e.data);
      onEvent('workflow_activated', data);
    });

    // Workflow failed
    this.eventSource.addEventListener('workflow_failed', (e) => {
      const data = JSON.parse(e.data);
      onEvent('workflow_failed', data);
    });

    // Heartbeat (keepalive) - don't log to reduce console noise
    this.eventSource.addEventListener('heartbeat', () => {
      // Silent - heartbeat is working
    });

    // Error handling with smart reconnect
    this.eventSource.onerror = (error) => {
      // Check if this is a Cloudflare timeout (connection lasted ~28s)
      const connectionDuration = Date.now() - this.lastConnectTime;
      const isCloudflareTimeout = connectionDuration >= 20000 && connectionDuration <= 35000;

      if (!isCloudflareTimeout) {
        // Real error - log it
        console.error('⚠️ SSE Connection error:', error);
        console.log('ReadyState:', this.eventSource?.readyState);
      }
      
      this.disconnect();
      
      // Always reconnect unless manually disconnected
      if (!this.isManualDisconnect) {
        this.reconnectAttempts++;
        
        // Only log reconnect for real errors (not Cloudflare timeout)
        if (!isCloudflareTimeout && this.reconnectAttempts > 1) {
          console.log(`🔄 Reconnecting... (attempt ${this.reconnectAttempts})`);
        }
        
        setTimeout(() => {
          if (!this.isManualDisconnect) {
            this.connect(token, onEvent);
          }
        }, this.reconnectDelay);
        
        // Exponential backoff for real errors only
        if (!isCloudflareTimeout) {
          this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 10000); // Max 10s
        }
      }
    };
  }

  disconnect() {
    this.isManualDisconnect = true;
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

export const sseService = new SSEService();
